// Sandbox guard (SM-752 / SM-758): the console must be provably unable
// to drive anything but the provider MOCK.
//
// Pure module — no DOM, no fetch — so it is unit-tested under
// `node --test`. The app consults it:
//
//   1. at boot, before any request, with the configured API base and the
//      page hostname (`evaluateConfig`): an absolute / cross-origin API
//      base, or a production-looking host, blocks the app outright and
//      no request is ever sent;
//   2. still before sign-in (the login page included), with the answer of
//      the unauthenticated probe `GET /mock-admin/environment/public`
//      (`{banner, environment_label}`) — the root route's beforeLoad, so
//      no other request precedes it;
//   3. after sign-in, with `GET /mock-admin/environment`, by the same rule.
//
// The rule (2, 3): the banner must read exactly "MOCK PROVIDER — SANDBOX"
// and the label must be one of the non-production labels the mock itself
// accepts (its ENVIRONMENT_LABEL refuses anything else at start-up).
// Until it holds the app renders only a blocking full-page error, so no
// page mounts and no other API call is made.

/** The banner the mock answers on `/mock-admin/environment`. */
export const SANDBOX_BANNER = "MOCK PROVIDER — SANDBOX";

/** The only API base the console accepts: same-origin, relative. */
export const SANDBOX_API_BASE = "/mock-admin";

/** Non-production labels (the mock's own ENVIRONMENT_LABEL allow-list). */
export const SANDBOX_LABELS = ["local", "dev", "test", "ci", "sit", "uat", "sandbox"] as const;

export type SandboxLabel = (typeof SANDBOX_LABELS)[number];

/** The fields of the environment answer the guard reads. */
export interface EnvironmentProbe {
  banner?: unknown;
  environment_label?: unknown;
}

export type GuardReason =
  | "API_BASE_NOT_RELATIVE"
  | "HOST_LOOKS_PRODUCTION"
  | "ENVIRONMENT_UNREADABLE"
  | "BANNER_MISSING"
  | "LABEL_NOT_SANDBOX";

export type GuardVerdict =
  | { ok: true; label: SandboxLabel | null }
  | { ok: false; reason: GuardReason; message: string };

export interface GuardInput {
  /** `window.location.hostname`. */
  hostname: string;
  /** The API base the bundle was built with. */
  apiBase: string;
  /**
   * The environment answer — of `/environment/public` before sign-in, of
   * `/environment` after. `undefined` = not probed (configuration-only
   * check); `null` = the probe failed or answered something not JSON.
   */
  environment?: EnvironmentProbe | null;
}

// A host label that names production. Delimited so "liveness" or
// "products" do not match, but "prod.example.com", "api-prod",
// "app.production.x" and "live.example.com" do.
const PRODUCTION_HOST = /(^|[.-])(prod|production|live)([.-]|$)/i;

/** True for the exact same-origin relative base `/mock-admin`. */
export function isSameOriginRelative(apiBase: string): boolean {
  const base = apiBase.trim().replace(/\/+$/, "");
  return base === SANDBOX_API_BASE;
}

export function isSandboxLabel(label: unknown): label is SandboxLabel {
  return typeof label === "string" && (SANDBOX_LABELS as readonly string[]).includes(label);
}

/**
 * The configuration half of the guard: what can be decided before any
 * request is sent.
 */
export function evaluateConfig(hostname: string, apiBase: string): GuardVerdict {
  if (!isSameOriginRelative(apiBase)) {
    return {
      ok: false,
      reason: "API_BASE_NOT_RELATIVE",
      message: `The console's API base is "${apiBase}". It must be the same-origin relative path ${SANDBOX_API_BASE}, served by this console's own proxy to the provider mock — an absolute or cross-origin API base could point at a real provider.`,
    };
  }
  if (PRODUCTION_HOST.test(hostname)) {
    return {
      ok: false,
      reason: "HOST_LOOKS_PRODUCTION",
      message: `This console is served from "${hostname}", which names a production host. The provider mock console runs in sandbox environments only.`,
    };
  }
  return { ok: true, label: null };
}

/**
 * The full guard: configuration plus, once probed, the environment
 * answer. `environment === undefined` (not probed yet) passes with a
 * null label so the sign-in form can render; every other path that is
 * not provably the sandbox refuses.
 */
export function evaluateSandbox(input: GuardInput): GuardVerdict {
  const config = evaluateConfig(input.hostname, input.apiBase);
  if (!config.ok) return config;
  if (input.environment === undefined) return config;
  const env = input.environment;
  if (env === null || typeof env !== "object") {
    return {
      ok: false,
      reason: "ENVIRONMENT_UNREADABLE",
      message: "The environment probe did not answer a readable environment, so this console cannot prove it is talking to the provider mock.",
    };
  }
  if (env.banner !== SANDBOX_BANNER) {
    return {
      ok: false,
      reason: "BANNER_MISSING",
      message: `The API did not identify itself as "${SANDBOX_BANNER}". Whatever answered is not the provider mock, so the console refuses to run against it.`,
    };
  }
  if (!isSandboxLabel(env.environment_label)) {
    const shown = typeof env.environment_label === "string" ? env.environment_label : String(env.environment_label);
    return {
      ok: false,
      reason: "LABEL_NOT_SANDBOX",
      message: `The environment label is "${shown}". The console runs only against ${SANDBOX_LABELS.join(", ")}.`,
    };
  }
  return { ok: true, label: env.environment_label };
}
